// frontend/src/pages/StartSubscriptionPage.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header/Header';
import Background from '../components/Background/Background';
import SearchBar, { SearchBarProps } from '../components/SearchBar/SearchBar';
import helixImg from '../assets/helix.png';
import '../styles/StartSubscriptionPage.css';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchWithAuth } from '../api/fetchWithAuth';

const StartSubscriptionPage: React.FC = () => {
  const [ssn, setSsn] = useState('');
  const [patientName, setPatientName] = useState('');
  const [phone, setPhone] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState<'sott' | 'sent' | null>(null);
  const [priority, setPriority] = useState<'venjuleg' | 'flyti' | null>(null);
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [store, setStore] = useState('');
  const [dispenser, setDispenser] = useState<'Lyfja' | 'Lyfjaver' | 'SA lyfjaskömmtun' | null>(
    null
  );

  const [searchSsn, setSearchSsn] = useState('');
  const [activeTab, setActiveTab] = useState<'medications' | 'history'>('medications');
  const navigate = useNavigate();

  const API_BASE = 'https://api-staging.mitoflux.is';
  const { getAccessTokenSilently } = useAuth0();

  const canContinue =
    !!ssn.trim() &&
    !!deliveryMethod &&
    !!priority &&
    !!dispenser &&
    (deliveryMethod === 'sent'
      ? !!address.trim() && !!postalCode.trim() && !!city.trim()
      : !!store.trim());

  const storeOptions = React.useMemo(() => {
    switch (dispenser) {
      case 'Lyfja':
        return ['Lyfja Kringlan', 'Lyfja Smáralind', 'Lyfja Lágmúli'];
      case 'Lyfjaver':
        return ['Akureyrarapótek', 'Rima Apótek', 'Lyfjaver Suðurlandsbraut'];
      case 'SA lyfjaskömmtun':
        return [
          'Lyf og heilsa Kringlan',
          'Lyf og heilsa Granda',
          'Lyf og heilsa Firði',
          'Lyf og heilsa Glerártorgi',
        ];
      default:
        return [];
    }
  }, [dispenser]);

  React.useEffect(() => {
    // If switching delivery method, clear the other mode's fields
    if (deliveryMethod === 'sent') setStore('');
    if (deliveryMethod === 'sott') {
      setAddress('');
      setPostalCode('');
      setCity('');
    }
  }, [deliveryMethod]);

  React.useEffect(() => {
    // If dispenser changes, clear store selection so user re-picks
    setStore('');
  }, [dispenser]);

  const handleContinue = async () => {
    if (!ssn.trim()) return;

    const res = await fetchWithAuth(getAccessTokenSilently, `${API_BASE}/api/patients/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ssn: ssn.trim() }),
    });

    if (!(res.ok || res.status === 404)) {
      const text = await res.text();
      throw new Error(`API ${res.status}: ${text}`);
    }

    const data = await res.json(); // { found, patientId, ... }
    const patientId = data.patientId as string;
    if (!patientId) throw new Error('Missing patientId from /patients/search');

    // resolve pharmacy (store is optional if delivery is "sent")
    const resolveRes = await fetchWithAuth(
      getAccessTokenSilently,
      `${API_BASE}/api/pharmacies/resolve`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dispenser,
          store: deliveryMethod === 'sott' ? store : null,
        }),
      }
    );

    if (!resolveRes.ok) {
      const text = await resolveRes.text();
      throw new Error(`Resolve pharmacy ${resolveRes.status}: ${text}`);
    }

    const resolved = await resolveRes.json(); // {pharmacyId, pharmacyOrgId, pharmacyName}

    navigate('/doctor/patient/new', {
      state: {
        mode: 'new-patient',
        patientId,
        name: patientName,
        ssn,
        phone,
        deliveryMethod,
        priority,
        dispenser,
        address,
        postalCode,
        city,
        store: deliveryMethod === 'sott' ? store : '',
        pharmacyId: resolved.pharmacyId,
        pharmacyOrgId: resolved.pharmacyOrgId,
        pharmacyName: resolved.pharmacyName,
        pharmacySiteId: resolved.siteId ?? null, // optional, maybe use later
      },
    });
  };

  const handleSearch = async () => {
    const ssnTrimmed = searchSsn.trim();
    if (!ssnTrimmed) return;
    if (!/^\d{10}$/.test(ssnTrimmed)) {
      alert('Kennitala verður að vera nákvæmlega 10 tölustafir');
      return;
    }

    try {
      const res = await fetchWithAuth(getAccessTokenSilently, `${API_BASE}/api/patients/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ssn: ssnTrimmed }),
      });

      if (res.status === 404) {
        navigate('/notfound');
        return;
      }

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API ${res.status}: ${text}`);
      }

      const data = await res.json();
      const newPatientId = data.patientId as string | undefined;
      if (!newPatientId) throw new Error('Missing patientId in search response');

      navigate(`/doctor/patient/${encodeURIComponent(newPatientId)}`, {
        state: { name: data.name, pharmacyName: data.pharmacyName ?? data.pharmacy ?? '' },
      });
    } catch (e) {
      console.error(e);
      navigate('/notfound');
    }
  };

  const searchProps: SearchBarProps = {
    value: searchSsn,
    onChange: setSearchSsn,
    onSearch: handleSearch,
    onMenuClick: () => console.log('menu'),
  };

  return (
    <>
      <Header center={<SearchBar {...searchProps} />} />

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'medications' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('medications');
            navigate('/doctor');
          }}
        >
          Lyfjaskammtanir
        </button>
        <button
          className={`tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('history');
            navigate('/doctor/changes');
          }}
        >
          Þínar fyrri breytingar
        </button>
      </div>

      <Background>
        <img src={helixImg} alt="Helix" className="background-helix" />
        <div className="start-subscription-container">
          <h2 className="title">Hefja lyfjaskömmtun</h2>
          <hr className="divider" />

          <div className="form-group">
            <label>Kennitala</label>
            <input type="text" value={ssn} onChange={(e) => setSsn(e.target.value)} />
          </div>

          <div className="form-group">
            <label>Nafn sjúklings</label>
            <input
              type="text"
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Símanúmer sjúklings</label>
            <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>

          <div className="form-group">
            <label>Afgreiðsla</label>
            <div className="button-group">
              <button
                className={priority === 'venjuleg' ? 'selected' : ''}
                onClick={() => setPriority('venjuleg')}
              >
                Venjuleg
              </button>
              <button
                className={priority === 'flyti' ? 'selected' : ''}
                onClick={() => setPriority('flyti')}
              >
                Flýti
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>
              Skömmtunaraðili
              <span className="tooltip-icon" title="Aðili sem sér um skömmtunina">
                ⓘ
              </span>
            </label>
            <div className="button-group">
              <button
                className={dispenser === 'Lyfja' ? 'selected' : ''}
                onClick={() => setDispenser('Lyfja')}
              >
                Lyfja
              </button>
              <button
                className={dispenser === 'Lyfjaver' ? 'selected' : ''}
                onClick={() => setDispenser('Lyfjaver')}
              >
                Lyfjaver
              </button>
              <button
                className={dispenser === 'SA lyfjaskömmtun' ? 'selected' : ''}
                onClick={() => setDispenser('SA lyfjaskömmtun')}
              >
                SA lyfjaskömmtun
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Sótt eða sent</label>
            <div className="button-group">
              <button
                className={deliveryMethod === 'sott' ? 'selected' : ''}
                onClick={() => setDeliveryMethod('sott')}
                type="button"
              >
                Sótt
              </button>
              <button
                className={deliveryMethod === 'sent' ? 'selected' : ''}
                onClick={() => setDeliveryMethod('sent')}
                type="button"
              >
                Sent
              </button>
            </div>
          </div>

          {deliveryMethod === 'sent' && (
            <>
              <div className="form-group">
                <label>Heimilisfang</label>
                <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} />
              </div>

              <div className="form-group">
                <label>Póstnúmer</label>
                <input
                  type="text"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Bær</label>
                <input type="text" value={city} onChange={(e) => setCity(e.target.value)} />
              </div>
            </>
          )}

          {deliveryMethod === 'sott' && (
            <div className="form-group">
              <label>Verslun</label>
              <select
                value={store}
                onChange={(e) => setStore(e.target.value)}
                disabled={!dispenser}
              >
                <option value="">
                  {dispenser ? 'Veldu verslun' : 'Veldu skömmtunaraðila fyrst'}
                </option>

                {storeOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            className="start-subscription-continue"
            disabled={!canContinue}
            onClick={() => {
              handleContinue().catch((e) => {
                console.error(e);
                alert('Ekki tókst að hefja skráningu.');
              });
            }}
          >
            Halda áfram →
          </button>
        </div>
      </Background>
    </>
  );
};

export default StartSubscriptionPage;
